import { IMembership } from '@fff-web/fff-utilities';
import { SubscriptionStatus } from '../MyAccountPage';

export const SUBSCRIPTION_STATUS = {
  expired: 'EXPIRED',
  canceled: 'CANCELED',
  active: 'ACTIVE',
};

export const SUBSCRIPTION = {
  annual: 'annual',
  seasonal: 'seasonal',
  trial: 'trial',
};

export const PLAN_CODE = {
  ffftrial: 'ffftrial',
  fffvip: 'fffvip',
  fffvipa: 'fffvipa',
};

type SubscriptionPlan = (typeof SUBSCRIPTION)[keyof typeof SUBSCRIPTION] | '';

export type MembershipType = {
  isAnnual: boolean;
  isSeasonal: boolean;
  isTrial: boolean;
  isRefill: boolean;
  /** Legacy `subscriptionPlan` literal — formerly read from the `/accounts` subscription field. */
  subscriptionPlan: SubscriptionPlan;
};

const hasFlag = (flags: IMembership['flags'] | undefined, name: string) =>
  (flags ?? []).some((flag) => flag.display_name === name);

/**
 * Flags that mark a member as holding a core membership box in ANY state — active
 * (`has_box_subscription` plus the plan-specific `has_annual_box_subscription` /
 * `has_seasonal_box_subscription`) or lapsed (`expired_box_subscription` /
 * `canceled_box_subscription`). The plan-specific active flags mirror asado's `ACTIVE_BOX_FLAGS`;
 * the expired/canceled flags keep the classification state-agnostic, matching the legacy
 * `/accounts` `planCode` which labeled annual/seasonal regardless of subscription state.
 */
const CORE_BOX_FLAGS = [
  'has_box_subscription',
  'has_annual_box_subscription',
  'has_seasonal_box_subscription',
  'expired_box_subscription',
  'canceled_box_subscription',
];

const hasCoreBox = (flags: IMembership['flags'] | undefined) =>
  (flags ?? []).some((flag) => CORE_BOX_FLAGS.includes(flag.display_name));

/**
 * Derive the member's subscription type from the membership endpoint (`/users/memberships/self`)
 * tier + flags, replacing the legacy `/accounts` `subscription`/`planCode` fields (FP-1322). Mirrors
 * the canonical rule the navbar BFF uses (asado `getMembershipInternalType`): the tier (gold/silver)
 * decides annual vs seasonal, gated by holding a core box; trial takes precedence via the `trial`
 * flag.
 */
export const getMembershipType = (membershipInfo?: IMembership): MembershipType => {
  const tier = membershipInfo?.tier?.display_name;
  const flags = membershipInfo?.flags;

  const isTrial = hasFlag(flags, 'trial');
  const isAnnual = tier === 'gold' && hasCoreBox(flags);
  const isSeasonal = tier === 'silver' && hasCoreBox(flags);
  const isRefill = !!membershipInfo?.isRefillSubscription;

  const subscriptionPlan: SubscriptionPlan = isTrial
    ? SUBSCRIPTION.trial
    : isAnnual
      ? SUBSCRIPTION.annual
      : isSeasonal
        ? SUBSCRIPTION.seasonal
        : '';

  return { isAnnual, isSeasonal, isTrial, isRefill, subscriptionPlan };
};

export const getUserSubscriptionStatus = (subscriptionState: SubscriptionStatus) => {
  if (subscriptionState === 'ACTIVE') {
    return {
      SUBSCRIPTION_STATE: subscriptionState,
      COLOR: 'green',
    };
  }
  if (subscriptionState === 'EXPIRED') {
    return {
      SUBSCRIPTION_STATE: subscriptionState,
      COLOR: 'red',
    };
  }
  if (subscriptionState === 'CANCELED') {
    return {
      SUBSCRIPTION_STATE: subscriptionState,
      COLOR: 'grey',
    };
  }
  return {
    SUBSCRIPTION_STATE: subscriptionState,
    COLOR: 'green',
  };
};
